import type { EChartsOption } from 'echarts';
import type { SeriesBarChartConfig, SeriesChartDataItem } from './types';
import { buildAnimationConfig } from '../shared/animation';
import { buildCategoryAxis } from '../shared/axis';
import { buildSeriesBarSeries } from './series';

// Small headroom above the tallest element so the average line (and the "Average" text
// that sits just above it on the left) clears the top edge without a large empty band.
const TOP_HEADROOM = 0.08;
const BOTTOM_HEADROOM = 0.1;

export const buildSeriesBarChartOption = (data: SeriesChartDataItem[], config: SeriesBarChartConfig): EChartsOption => {
  const { theme, animated = true, average } = config;

  const categories = data.map((d) => d.month);

  const amounts = data.flatMap((d) => [d.totalAmount, d.currentAmount]);
  let min = Math.min(0, ...amounts);
  let max = Math.max(0, average, ...amounts);
  const range = max - min || 1;
  max += range * TOP_HEADROOM;
  if (min < 0) min -= range * BOTTOM_HEADROOM;

  return {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    // left margin holds the "Average" pill beside the plot (amcharts4 valueAxis.width = 72);
    // right padding mirrors amcharts4 categoryAxis.paddingRight = 54.
    grid: { left: 78, right: 54, top: 16, bottom: 8, containLabel: true },
    xAxis: {
      ...buildCategoryAxis(categories, theme),
      axisLabel: {
        color: theme.textSecondaryColor,
        fontFamily: theme.fontFamily,
        opacity: 0.6,
        fontSize: 12,
        interval: 0,
        margin: 12,
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min,
      max,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    series: buildSeriesBarSeries(data, config),
  };
};
